import React, { useState } from 'react';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  TableHead,
  CircularProgress,
  Alert,
  IconButton,
  Paper,
  Chip,
  Grid
} from '@mui/material';
import { ExpandMore as ExpandMoreIcon, ExpandLess as ExpandLessIcon } from '@mui/icons-material';
import {
  BalanceSheetData,
  BalanceSheetPrimaryGroup,
  BalanceSheetSubGroup,
  BalanceSheetLedger
} from '../../utils/report.types';

interface BalanceSheetPartyWiseProps {
  data: BalanceSheetData | null;
  loading?: boolean;
  error?: string;
}

const BalanceSheetPartyWise: React.FC<BalanceSheetPartyWiseProps> = ({ data, loading, error }) => {
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set());
  const [expandedSubGroups, setExpandedSubGroups] = useState<Set<string>>(new Set());
  const [expandedLedgers, setExpandedLedgers] = useState<Set<string>>(new Set());

  const formatAmount = (amount: number) => {
    return new Intl.NumberFormat('en-IN', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(Math.abs(amount));
  };

  const toggleGroup = (groupId: string) => {
    const newExpanded = new Set(expandedGroups);
    if (newExpanded.has(groupId)) {
      newExpanded.delete(groupId);
    } else {
      newExpanded.add(groupId);
    }
    setExpandedGroups(newExpanded);
  };

  const toggleSubGroup = (subGroupId: string) => {
    const newExpanded = new Set(expandedSubGroups);
    if (newExpanded.has(subGroupId)) {
      newExpanded.delete(subGroupId);
    } else {
      newExpanded.add(subGroupId);
    }
    setExpandedSubGroups(newExpanded);
  };

  const toggleLedger = (ledgerId: string) => {
    const newExpanded = new Set(expandedLedgers);
    if (newExpanded.has(ledgerId)) {
      newExpanded.delete(ledgerId);
    } else {
      newExpanded.add(ledgerId);
    }
    setExpandedLedgers(newExpanded);
  };

  const renderParties = (ledger: BalanceSheetLedger) => {
    if (!ledger.parties || ledger.parties.length === 0) return null;
    const isExpanded = expandedLedgers.has(ledger._id);

    return (
      <>
        <TableRow
          hover
          onClick={() => toggleLedger(ledger._id)}
          sx={{ cursor: 'pointer', '&:hover': { backgroundColor: '#f9f9f9' } }}
        >
          <TableCell sx={{ pl: 6, py: 0.75, fontSize: '0.813rem', borderBottom: '1px solid #e0e0e0' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <IconButton size="small" sx={{ padding: 0, width: 20, height: 20 }}>
                {isExpanded ? <ExpandLessIcon sx={{ fontSize: 16 }} /> : <ExpandMoreIcon sx={{ fontSize: 16 }} />}
              </IconButton>
              <Typography sx={{ fontSize: '0.813rem', fontWeight: 500 }}>{ledger.name}</Typography>
            </Box>
          </TableCell>
          <TableCell align="right" sx={{ py: 0.75, fontSize: '0.813rem', borderBottom: '1px solid #e0e0e0' }}>
            {formatAmount(ledger.debit)}
          </TableCell>
          <TableCell align="right" sx={{ py: 0.75, fontSize: '0.813rem', borderBottom: '1px solid #e0e0e0' }}>
            {formatAmount(ledger.credit)}
          </TableCell>
          <TableCell align="right" sx={{ py: 0.75, fontSize: '0.813rem', fontWeight: 600, borderBottom: '1px solid #e0e0e0' }}>
            {formatAmount(ledger.balance)}
          </TableCell>
        </TableRow>
        {isExpanded && ledger.parties.map((party) => (
          <TableRow key={party._id} sx={{ backgroundColor: '#fafafa' }}>
            <TableCell sx={{ pl: 10, py: 0.5, fontSize: '0.75rem', color: '#666', borderBottom: '1px solid #f0f0f0' }}>
              → {party.name}
            </TableCell>
            <TableCell align="right" sx={{ py: 0.5, fontSize: '0.75rem', color: '#666', borderBottom: '1px solid #f0f0f0' }}>
              {formatAmount(party.debit)}
            </TableCell>
            <TableCell align="right" sx={{ py: 0.5, fontSize: '0.75rem', color: '#666', borderBottom: '1px solid #f0f0f0' }}>
              {formatAmount(party.credit)}
            </TableCell>
            <TableCell align="right" sx={{ py: 0.5, fontSize: '0.75rem', color: '#666', fontWeight: 500, borderBottom: '1px solid #f0f0f0' }}>
              {formatAmount(party.balance)}
            </TableCell>
          </TableRow>
        ))}
      </>
    );
  };

  const renderLedgers = (ledgers: BalanceSheetLedger[]) => {
    if (!ledgers || ledgers.length === 0) return null;
    return ledgers.map((ledger) => {
      if (ledger.parties && ledger.parties.length > 0) {
        return renderParties(ledger);
      }
      return (
        <TableRow key={ledger._id} hover sx={{ '&:hover': { backgroundColor: '#f9f9f9' } }}>
          <TableCell sx={{ pl: 6, py: 0.75, fontSize: '0.813rem', borderBottom: '1px solid #e0e0e0' }}>
            {ledger.name}
          </TableCell>
          <TableCell align="right" sx={{ py: 0.75, fontSize: '0.813rem', borderBottom: '1px solid #e0e0e0' }}>
            {formatAmount(ledger.debit)}
          </TableCell>
          <TableCell align="right" sx={{ py: 0.75, fontSize: '0.813rem', borderBottom: '1px solid #e0e0e0' }}>
            {formatAmount(ledger.credit)}
          </TableCell>
          <TableCell align="right" sx={{ py: 0.75, fontSize: '0.813rem', fontWeight: 500, borderBottom: '1px solid #e0e0e0' }}>
            {formatAmount(ledger.balance)}
          </TableCell>
        </TableRow>
      );
    });
  };

  const renderSubGroups = (subGroups: BalanceSheetSubGroup[]) => {
    if (!subGroups || subGroups.length === 0) return null;
    return subGroups.map((subGroup) => {
      const isExpanded = expandedSubGroups.has(subGroup._id);
      return (
        <React.Fragment key={subGroup._id}>
          <TableRow
            hover
            onClick={() => toggleSubGroup(subGroup._id)}
            sx={{ cursor: 'pointer', backgroundColor: '#f5f5f5', '&:hover': { backgroundColor: '#eeeeee' } }}
          >
            <TableCell sx={{ pl: 4, py: 0.75, fontSize: '0.813rem', fontWeight: 600, borderBottom: '1px solid #e0e0e0' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <IconButton size="small" sx={{ padding: 0, width: 20, height: 20 }}>
                  {isExpanded ? <ExpandLessIcon sx={{ fontSize: 16 }} /> : <ExpandMoreIcon sx={{ fontSize: 16 }} />}
                </IconButton>
                <Typography sx={{ fontSize: '0.813rem', fontWeight: 600 }}>{subGroup.name}</Typography>
              </Box>
            </TableCell>
            <TableCell align="right" sx={{ py: 0.75, fontSize: '0.813rem', borderBottom: '1px solid #e0e0e0' }}></TableCell>
            <TableCell align="right" sx={{ py: 0.75, fontSize: '0.813rem', borderBottom: '1px solid #e0e0e0' }}></TableCell>
            <TableCell align="right" sx={{ py: 0.75, fontSize: '0.813rem', fontWeight: 600, borderBottom: '1px solid #e0e0e0' }}>
              {formatAmount(subGroup.total)}
            </TableCell>
          </TableRow>
          {isExpanded && renderLedgers(subGroup.ledgers)}
        </React.Fragment>
      );
    });
  };

  const renderPrimaryGroups = (groups: BalanceSheetPrimaryGroup[]) => {
    if (!groups || groups.length === 0) return null;
    return groups.map((group) => {
      const isExpanded = expandedGroups.has(group._id);
      return (
        <React.Fragment key={group._id}>
          <TableRow
            hover
            onClick={() => toggleGroup(group._id)}
            sx={{ cursor: 'pointer', '&:hover': { backgroundColor: '#f0f0f0' } }}
          >
            <TableCell sx={{ pl: 2, py: 1, fontSize: '0.875rem', fontWeight: 700, borderBottom: '1px solid #d0d0d0' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <IconButton size="small" sx={{ padding: 0, width: 20, height: 20 }}>
                  {isExpanded ? <ExpandLessIcon sx={{ fontSize: 18 }} /> : <ExpandMoreIcon sx={{ fontSize: 18 }} />}
                </IconButton>
                <Typography sx={{ fontSize: '0.875rem', fontWeight: 700 }}>{group.name}</Typography>
              </Box>
            </TableCell>
            <TableCell align="right" sx={{ py: 1, fontSize: '0.813rem', borderBottom: '1px solid #d0d0d0' }}></TableCell>
            <TableCell align="right" sx={{ py: 1, fontSize: '0.813rem', borderBottom: '1px solid #d0d0d0' }}></TableCell>
            <TableCell align="right" sx={{ py: 1, fontSize: '0.875rem', fontWeight: 700, borderBottom: '1px solid #d0d0d0' }}>
              {formatAmount(group.total)}
            </TableCell>
          </TableRow>
          {isExpanded && (
            <>
              {group.subGroups && renderSubGroups(group.subGroups)}
              {group.ledgers && renderLedgers(group.ledgers)}
            </>
          )}
        </React.Fragment>
      );
    });
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  if (!data) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="info">No data available. Please select a date range and generate the report.</Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ width: '100%', p: 1, backgroundColor: '#fff' }}>
      {/* Two Column Layout */}
      <Grid container spacing={2}>
        {/* Left Column - Assets & Equity */}
        <Grid item xs={12} md={6}>
          {/* ASSETS */}
          <TableContainer component={Paper} variant="outlined" sx={{ mb: 2 }}>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ backgroundColor: '#1976d2' }}>
                  <TableCell sx={{ color: 'white', fontWeight: 700, fontSize: '0.875rem', py: 1 }}>
                    ASSETS
                  </TableCell>
                  <TableCell align="right" sx={{ color: 'white', fontWeight: 700, fontSize: '0.75rem', py: 1, width: '15%' }}>
                    Debit
                  </TableCell>
                  <TableCell align="right" sx={{ color: 'white', fontWeight: 700, fontSize: '0.75rem', py: 1, width: '15%' }}>
                    Credit
                  </TableCell>
                  <TableCell align="right" sx={{ color: 'white', fontWeight: 700, fontSize: '0.75rem', py: 1, width: '20%' }}>
                    Balance
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {renderPrimaryGroups(data.assets.groups)}
                <TableRow sx={{ backgroundColor: '#e3f2fd' }}>
                  <TableCell sx={{ fontWeight: 700, fontSize: '0.875rem', py: 1, borderTop: '2px solid #1976d2' }}>
                    TOTAL ASSETS
                  </TableCell>
                  <TableCell sx={{ borderTop: '2px solid #1976d2' }}></TableCell>
                  <TableCell sx={{ borderTop: '2px solid #1976d2' }}></TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700, fontSize: '0.875rem', py: 1, borderTop: '2px solid #1976d2' }}>
                    {formatAmount(data.assets.total)}
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>

          {/* EQUITY */}
          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow sx={{ backgroundColor: '#388e3c' }}>
                  <TableCell sx={{ color: 'white', fontWeight: 700, fontSize: '0.875rem', py: 1 }}>
                    EQUITY (P&L)
                  </TableCell>
                  <TableCell align="right" sx={{ color: 'white', fontWeight: 700, fontSize: '0.75rem', py: 1, width: '15%' }}>
                    Debit
                  </TableCell>
                  <TableCell align="right" sx={{ color: 'white', fontWeight: 700, fontSize: '0.75rem', py: 1, width: '15%' }}>
                    Credit
                  </TableCell>
                  <TableCell align="right" sx={{ color: 'white', fontWeight: 700, fontSize: '0.75rem', py: 1, width: '20%' }}>
                    Balance
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                <TableRow>
                  <TableCell sx={{ pl: 2, py: 0.75, fontSize: '0.813rem', fontWeight: 500, borderBottom: '1px solid #e0e0e0' }}>
                    Revenue
                  </TableCell>
                  <TableCell align="right" sx={{ py: 0.75, fontSize: '0.813rem', borderBottom: '1px solid #e0e0e0' }}></TableCell>
                  <TableCell align="right" sx={{ py: 0.75, fontSize: '0.813rem', color: '#2e7d32', fontWeight: 500, borderBottom: '1px solid #e0e0e0' }}>
                    {formatAmount(data.equity.revenue)}
                  </TableCell>
                  <TableCell align="right" sx={{ py: 0.75, fontSize: '0.813rem', color: '#2e7d32', fontWeight: 600, borderBottom: '1px solid #e0e0e0' }}>
                    {formatAmount(data.equity.revenue)}
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell sx={{ pl: 2, py: 0.75, fontSize: '0.813rem', fontWeight: 500, borderBottom: '1px solid #e0e0e0' }}>
                    Expenses
                  </TableCell>
                  <TableCell align="right" sx={{ py: 0.75, fontSize: '0.813rem', color: '#c62828', fontWeight: 500, borderBottom: '1px solid #e0e0e0' }}>
                    {formatAmount(data.equity.expenses)}
                  </TableCell>
                  <TableCell align="right" sx={{ py: 0.75, fontSize: '0.813rem', borderBottom: '1px solid #e0e0e0' }}></TableCell>
                  <TableCell align="right" sx={{ py: 0.75, fontSize: '0.813rem', color: '#c62828', fontWeight: 600, borderBottom: '1px solid #e0e0e0' }}>
                    ({formatAmount(data.equity.expenses)})
                  </TableCell>
                </TableRow>
                <TableRow sx={{ backgroundColor: data.equity.netProfit >= 0 ? '#e8f5e9' : '#ffebee' }}>
                  <TableCell sx={{ fontWeight: 700, fontSize: '0.875rem', py: 1, borderTop: '2px solid #388e3c' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography sx={{ fontWeight: 700, fontSize: '0.875rem' }}>
                        NET {data.equity.netProfit >= 0 ? 'PROFIT' : 'LOSS'}
                      </Typography>
                      <Chip
                        label={data.equity.netProfit >= 0 ? 'Profit' : 'Loss'}
                        size="small"
                        sx={{
                          height: 18,
                          fontSize: '0.65rem',
                          backgroundColor: data.equity.netProfit >= 0 ? '#4caf50' : '#f44336',
                          color: 'white',
                          fontWeight: 600
                        }}
                      />
                    </Box>
                  </TableCell>
                  <TableCell sx={{ borderTop: '2px solid #388e3c' }}></TableCell>
                  <TableCell sx={{ borderTop: '2px solid #388e3c' }}></TableCell>
                  <TableCell
                    align="right"
                    sx={{
                      fontWeight: 700,
                      fontSize: '0.875rem',
                      py: 1,
                      borderTop: '2px solid #388e3c',
                      color: data.equity.netProfit >= 0 ? '#2e7d32' : '#c62828'
                    }}
                  >
                    {formatAmount(data.equity.netProfit)}
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>
        </Grid>

        {/* Right Column - Liabilities */}
        <Grid item xs={12} md={6}>
          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow sx={{ backgroundColor: '#d32f2f' }}>
                  <TableCell sx={{ color: 'white', fontWeight: 700, fontSize: '0.875rem', py: 1 }}>
                    LIABILITIES
                  </TableCell>
                  <TableCell align="right" sx={{ color: 'white', fontWeight: 700, fontSize: '0.75rem', py: 1, width: '15%' }}>
                    Debit
                  </TableCell>
                  <TableCell align="right" sx={{ color: 'white', fontWeight: 700, fontSize: '0.75rem', py: 1, width: '15%' }}>
                    Credit
                  </TableCell>
                  <TableCell align="right" sx={{ color: 'white', fontWeight: 700, fontSize: '0.75rem', py: 1, width: '20%' }}>
                    Balance
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {renderPrimaryGroups(data.liabilities.groups)}
                <TableRow sx={{ backgroundColor: '#ffebee' }}>
                  <TableCell sx={{ fontWeight: 700, fontSize: '0.875rem', py: 1, borderTop: '2px solid #d32f2f' }}>
                    TOTAL LIABILITIES
                  </TableCell>
                  <TableCell sx={{ borderTop: '2px solid #d32f2f' }}></TableCell>
                  <TableCell sx={{ borderTop: '2px solid #d32f2f' }}></TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700, fontSize: '0.875rem', py: 1, borderTop: '2px solid #d32f2f' }}>
                    {formatAmount(data.liabilities.total)}
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>
        </Grid>
      </Grid>

      {/* Balance Verification Footer */}
      <Box sx={{ mt: 2, p: 1.5, backgroundColor: data.balanceCheck.isBalanced ? '#e8f5e9' : '#ffebee', border: `2px solid ${data.balanceCheck.isBalanced ? '#4caf50' : '#f44336'}`, borderRadius: 1 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={8}>
            <Typography variant="body2" sx={{ fontWeight: 700, color: data.balanceCheck.isBalanced ? '#2e7d32' : '#c62828' }}>
              {data.balanceCheck.isBalanced ? '✓ Balanced' : '✗ Not Balanced'}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {data.balanceCheck.formula}
            </Typography>
          </Grid>
          <Grid item xs={6} md={2}>
            <Typography variant="caption" color="text.secondary">Assets</Typography>
            <Typography variant="body2" sx={{ fontWeight: 700 }}>{formatAmount(data.balanceCheck.leftSide)}</Typography>
          </Grid>
          <Grid item xs={6} md={2}>
            <Typography variant="caption" color="text.secondary">Liabilities + Equity</Typography>
            <Typography variant="body2" sx={{ fontWeight: 700 }}>{formatAmount(data.balanceCheck.rightSide)}</Typography>
          </Grid>
        </Grid>
      </Box>
    </Box>
  );
};

export default BalanceSheetPartyWise;
